import {useCallback, useEffect, useState} from 'react';

import {useLazyUnmountCancelToken} from '../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../libs/shared/core/src/helpers/types/asyncStatuses';
import {postApiAsync} from '../../libs/shared/core/src/services/http/http';
import {useAppQuery} from '../../libs/shared/core/src/services/query/useAppQuery';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {ConversationSafetyNetModel} from '../models/api/conversationSafetyNetModel';
import {SafetyNetsEnum} from '../models/api/safetyNetModel';
import {ApiTagsPath, TagPathModel} from '../models/api/tagModel';
import {safetyNetQueries} from '../queries/safetyNetsQueries';
import {getCompany} from '../store/data/company/companySelectors';
import {useAppSelector} from './appReduxHelpers';

/*
 * SafetyNets.
 */

export function isSafetyNetSatisfied(
  safetyNet: ConversationSafetyNetModel,
  safetyNetType: SafetyNetsEnum,
): boolean {
  const {isOptimisticallySatisfied, isSatisfied, type} = safetyNet;

  if (type !== safetyNetType) {
    return true;
  }

  return isOptimisticallySatisfied ?? isSatisfied;
}

/*
 * Required Tagging.
 */

export function useFetchTagList(tagIds: ReadonlyArray<number>): {
  tagsPath?: ReadonlyArray<TagPathModel>;
  status: AsyncStatusesEnum;
  onRetry: () => void;
} {
  const company = useAppSelector(getCompany);

  const [tagsPath, setTagsPath] = useState<ReadonlyArray<TagPathModel>>();
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  const loadTags = useCallback(async () => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    try {
      const apiTagsPath = await postApiAsync(
        ApiTagsPath,
        `${company.url}/tags_batch`,
        {
          ids: tagIds,
        },
        {cancelToken: buildUnmountCancelToken()},
      );
      setTagsPath(apiTagsPath);
      setLoadingStatus(AsyncStatusesEnum.LOADED);
    } catch {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
    }
  }, [buildUnmountCancelToken, company.url, tagIds]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  return {tagsPath, status: loadingStatus, onRetry: loadTags};
}

export function useFetchSafetyNets(safetyNetIds: ReadonlyArray<number>) {
  const {data: safetyNets = [], asyncStatus, refetch} = useAppQuery(safetyNetQueries.ids(safetyNetIds));

  return {safetyNets, loadingStatus: asyncStatus, onRetry: refetch};
}

export function areConversationSafetyNetsSatisfied(
  conversation: ConversationBaseModel,
  safetyNetType: SafetyNetsEnum,
) {
  if (!conversation.safetyNets) {
    return true;
  }

  return conversation.safetyNets.every((safetyNet) => isSafetyNetSatisfied(safetyNet, safetyNetType));
}
